import type { TFunction } from 'i18next';
import type { ComparisonDeltaRow } from './computeComparisonDeltas';

/** Rule-based insight strings (no LLM). */
export function buildComparisonInsights(
  deltas: ComparisonDeltaRow[],
  workoutATitle: string,
  workoutBTitle: string,
  t: TFunction,
): string[] {
  const insights: string[] = [];
  const byKey = Object.fromEntries(deltas.map(d => [d.key, d]));

  const avgPwr = byKey.avg_power;
  if (avgPwr?.delta != null && Math.abs(avgPwr.delta) >= 3) {
    const winner = avgPwr.delta > 0 ? workoutATitle : workoutBTitle;
    insights.push(
      t('compare.insight.avgPower', {
        winner,
        delta: Math.abs(Math.round(avgPwr.delta)),
      }),
    );
  }

  const duration = byKey.duration;
  if (duration?.delta != null && Math.abs(duration.delta) >= 60) {
    const longer = duration.delta > 0 ? workoutATitle : workoutBTitle;
    const mins = Math.round(Math.abs(duration.delta) / 60);
    insights.push(
      t('compare.insight.duration', {
        longer,
        mins,
        plural: mins !== 1 ? 's' : '',
      }),
    );
  }

  const tss = byKey.tss;
  if (tss?.delta != null && Math.abs(tss.delta) >= 5) {
    const harder = tss.delta > 0 ? workoutATitle : workoutBTitle;
    insights.push(
      t('compare.insight.tss', {
        harder,
        delta: Math.abs(Math.round(tss.delta)),
      }),
    );
  }

  const vi = byKey.vi;
  if (vi?.delta != null && Math.abs(vi.delta) >= 0.05) {
    const varied = vi.delta > 0 ? workoutATitle : workoutBTitle;
    insights.push(t('compare.insight.vi', { varied }));
  }

  const cadence = byKey.avg_cadence;
  if (cadence?.delta != null && Math.abs(cadence.delta) < 3 && cadence.valueA != null && cadence.valueB != null) {
    insights.push(t('compare.insight.cadenceSimilar'));
  } else if (cadence?.delta != null && Math.abs(cadence.delta) >= 3) {
    const faster = cadence.delta > 0 ? workoutATitle : workoutBTitle;
    insights.push(
      t('compare.insight.cadence', {
        faster,
        delta: Math.abs(Math.round(cadence.delta)),
      }),
    );
  }

  const np = byKey.np;
  if (
    np?.delta != null &&
    Math.abs(np.delta) >= 5 &&
    (avgPwr?.delta == null || Math.abs(avgPwr.delta) < 3)
  ) {
    insights.push(
      t('compare.insight.np', { delta: Math.abs(Math.round(np.delta)) }),
    );
  }

  if (insights.length === 0) {
    insights.push(t('compare.noInsights'));
  }

  return insights.slice(0, 5);
}
